/**
 * Respostas rápidas no compositor da conversa (pedido da PoolTrack, 02/10:
 * "assim como no Blip"). Duas portas para a MESMA lista:
 * - digitar `/` no início da mensagem (ou depois de espaço) abre a lista em cima
 *   da caixa, filtrada pelo que vem depois da barra; ↑ ↓ escolhem, Enter insere,
 *   Esc fecha;
 * - o botão "Respostas rápidas" abre a lista com uma busca.
 *
 * Escolher só PÕE o texto na caixa, com as variáveis preenchidas, para o
 * atendente revisar — nunca envia. Variável sem valor nesta conversa fica
 * escrita (`{placa}`) e o aviso embaixo da caixa diz quais faltam.
 *
 * Um hook e não um componente: os dois compositores (o completo, em
 * chat-window, e o do atendimento, em atendimentos/composer) têm caixas e
 * estados de texto diferentes; o hook se pluga na caixa de cada um.
 */
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Zap } from 'lucide-react';
import {
  fillQuickReply,
  filterQuickReplies,
  unresolvedVariables,
  type QuickReply,
  type QuickReplyVariableValue,
} from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Spinner } from '@/components/ui/misc';
import { cn } from '@/lib/utils';
import { chatQuickRepliesOptions, quickReplyErrorMessage } from './quick-replies-queries';
import {
  findSlashTrigger,
  insertAtSelection,
  moveHighlight,
  quickReplyKeyAction,
  replaceSlashTrigger,
} from './quick-reply-trigger';

export interface QuickReplyComposer {
  /** chame primeiro no onKeyDown da caixa: `true` = a tecla era da lista */
  handleKeyDown: (e: KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) => boolean;
  /** props extras da caixa de texto (cursor, foco, acessibilidade) */
  textareaProps: {
    onSelect: (e: { currentTarget: HTMLTextAreaElement }) => void;
    onFocus: () => void;
    onBlur: () => void;
    'aria-autocomplete': 'list';
    'aria-expanded': boolean;
    'aria-controls': string | undefined;
  };
  /** chame no onChange da caixa, depois de gravar o texto */
  onTextChange: (el: HTMLTextAreaElement) => void;
  /** a lista da `/`, posicionada em cima do compositor (pai `relative`) */
  slashPopup: ReactNode;
  /** o botão "Respostas rápidas" com a lista e a busca */
  button: ReactNode;
  /** "Sem valor nesta conversa: {placa}" — embaixo da caixa */
  warning: ReactNode;
}

export function useQuickReplyComposer({
  chatId,
  enabled,
  text,
  setText,
  textareaRef,
  buttonVariant = 'icon',
}: {
  chatId: number;
  /** conversa encerrada / sem caixa de texto: nada é buscado */
  enabled: boolean;
  text: string;
  setText: (value: string) => void;
  textareaRef: RefObject<HTMLTextAreaElement | null>;
  /** 'icon' no compositor do /chats; 'label' no do atendimento (botões com texto) */
  buttonVariant?: 'icon' | 'label';
}): QuickReplyComposer {
  const query = useQuery({ ...chatQuickRepliesOptions(chatId), enabled });
  const replies = query.data?.replies ?? NO_REPLIES;
  const variables = query.data?.variables ?? NO_VARIABLES;

  const [caret, setCaret] = useState(text.length);
  const [focused, setFocused] = useState(false);
  /** a `/` que o Esc fechou — não reabre até a barra sumir */
  const [dismissedAt, setDismissedAt] = useState<number | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  /** onde o botão insere: a seleção da caixa no momento em que a lista abriu */
  const selectionRef = useRef({ start: text.length, end: text.length });

  const trigger = findSlashTrigger(text, caret);
  // a `/` só abre a lista quando há o que escolher: sem nenhuma resposta para
  // esta conversa, a barra é só uma barra
  const slashOpen =
    enabled &&
    focused &&
    !pickerOpen &&
    trigger !== null &&
    trigger.start !== dismissedAt &&
    replies.length > 0;
  const search = pickerOpen ? pickerQuery : (trigger?.query ?? '');
  const items = useMemo(() => filterQuickReplies(replies, search), [replies, search]);
  const open = slashOpen || pickerOpen;

  useEffect(() => setHighlight(0), [search, slashOpen, pickerOpen]);

  const listId = `quick-replies-${chatId}`;

  const focusAt = (position: number) => {
    window.requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(position, position);
    });
  };

  const closePicker = () => {
    setPickerOpen(false);
    setPickerQuery('');
  };

  const choose = (reply: QuickReply) => {
    const filled = fillQuickReply(reply.body, variables).text;
    let result: { text: string; caret: number };
    if (pickerOpen) result = insertAtSelection(text, selectionRef.current, filled);
    else if (trigger) result = replaceSlashTrigger(text, trigger, filled);
    else return;
    setText(result.text);
    setCaret(result.caret);
    setDismissedAt(null);
    closePicker();
    focusAt(result.caret);
  };

  const close = () => {
    if (pickerOpen) {
      closePicker();
      focusAt(selectionRef.current.end);
    } else if (trigger) {
      setDismissedAt(trigger.start);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>): boolean => {
    const action = quickReplyKeyAction(
      { key: e.key, shiftKey: e.shiftKey, isComposing: e.nativeEvent.isComposing },
      open
    );
    if (action === 'pass') return false;
    e.preventDefault();
    e.stopPropagation();
    if (action === 'down') setHighlight((h) => moveHighlight(h, 1, items.length));
    else if (action === 'up') setHighlight((h) => moveHighlight(h, -1, items.length));
    else if (action === 'close') close();
    else {
      // Enter/Tab: escolhe; com a lista vazia, só fecha — nunca envia
      const item = items[highlight];
      if (item) choose(item);
      else close();
    }
    return true;
  };

  const onTextChange = (el: HTMLTextAreaElement) => {
    const position = el.selectionStart ?? el.value.length;
    setCaret(position);
    if (findSlashTrigger(el.value, position) === null) setDismissedAt(null);
  };

  const list = (
    <QuickReplyList
      id={listId}
      items={items}
      highlight={highlight}
      onHighlight={setHighlight}
      onChoose={choose}
      variables={variables}
      empty={
        replies.length === 0 ? (
          <span>
            Nenhuma resposta rápida para esta conversa.{' '}
            <Link to="/settings/quick-replies" className="text-primary hover:underline">
              Ver as respostas rápidas
            </Link>
          </span>
        ) : (
          `Nenhuma resposta para “${search}”.`
        )
      }
    />
  );

  const slashPopup = slashOpen ? (
    <div
      className="absolute bottom-full left-2 right-2 z-30 mb-1 max-w-xl overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-lg"
      aria-label="Respostas rápidas"
    >
      <p className="border-b px-3 py-1.5 text-[11px] text-muted-foreground">
        Respostas rápidas · ↑ ↓ escolhem · Enter insere para revisar · Esc fecha
      </p>
      {list}
    </div>
  ) : null;

  const button = (
    <Popover
      open={pickerOpen}
      onOpenChange={(next) => {
        if (next) {
          const el = textareaRef.current;
          selectionRef.current = {
            start: el?.selectionStart ?? text.length,
            end: el?.selectionEnd ?? text.length,
          };
          setPickerOpen(true);
        } else {
          closePicker();
        }
      }}
    >
      <PopoverTrigger asChild>
        {buttonVariant === 'icon' ? (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label="Respostas rápidas"
            title="Respostas rápidas (ou digite / na mensagem)"
            className="mb-0.5 text-muted-foreground"
            disabled={!enabled}
          >
            <Zap />
          </Button>
        ) : (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            title="Respostas rápidas (ou digite / na mensagem)"
            disabled={!enabled}
          >
            <Zap />
            Respostas rápidas
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="start"
        className="w-[min(32rem,calc(100vw-2rem))] p-0"
        onOpenAutoFocus={(e) => e.preventDefault()}
        // ao fechar, o foco volta para a CAIXA (escolher já o pôs lá, no fim do
        // texto inserido) — e não para o botão, que é o padrão do Radix
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <div className="border-b p-2">
          <input
            autoFocus
            value={pickerQuery}
            onChange={(e) => setPickerQuery(e.target.value)}
            onKeyDown={(e) => {
              handleKeyDown(e);
            }}
            placeholder="Buscar por atalho ou título"
            aria-label="Buscar resposta rápida"
            aria-controls={listId}
            className="h-8 w-full rounded-md border border-input bg-card px-2.5 text-sm outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
          />
        </div>
        {query.isPending ? (
          <div className="flex justify-center py-4">
            <Spinner className="size-5" />
          </div>
        ) : query.isError ? (
          <p className="px-3 py-3 text-sm text-destructive">
            {quickReplyErrorMessage(query.error)}
          </p>
        ) : (
          list
        )}
        <p className="border-t px-3 py-1.5 text-[11px] text-muted-foreground">
          O texto entra na caixa para você revisar — nada é enviado sozinho.
        </p>
      </PopoverContent>
    </Popover>
  );

  const missing = unresolvedVariables(text);
  const warning =
    missing.length > 0 ? (
      <p className="text-[11px] text-[#AD6800]" role="status">
        Sem valor nesta conversa: {missing.join(', ')} — complete ou apague antes de enviar.
      </p>
    ) : null;

  return {
    handleKeyDown,
    textareaProps: {
      onSelect: (e) => setCaret(e.currentTarget.selectionStart ?? 0),
      onFocus: () => setFocused(true),
      onBlur: () => setFocused(false),
      'aria-autocomplete': 'list',
      'aria-expanded': slashOpen,
      'aria-controls': slashOpen ? listId : undefined,
    },
    onTextChange,
    slashPopup,
    button,
    warning,
  };
}

const NO_REPLIES: QuickReply[] = [];
const NO_VARIABLES: QuickReplyVariableValue[] = [];

function QuickReplyList({
  id,
  items,
  highlight,
  onHighlight,
  onChoose,
  variables,
  empty,
}: {
  id: string;
  items: QuickReply[];
  highlight: number;
  onHighlight: (index: number) => void;
  onChoose: (reply: QuickReply) => void;
  variables: QuickReplyVariableValue[];
  empty: ReactNode;
}) {
  const listRef = useRef<HTMLUListElement>(null);
  useEffect(() => {
    listRef.current
      ?.querySelector<HTMLElement>(`[data-index="${highlight}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [highlight]);

  if (items.length === 0) {
    return <p className="px-3 py-3 text-sm text-muted-foreground">{empty}</p>;
  }
  return (
    <ul
      ref={listRef}
      id={id}
      role="listbox"
      aria-label="Respostas rápidas"
      className="max-h-64 overflow-y-auto py-1"
    >
      {items.map((reply, i) => (
        <li
          key={reply.id}
          role="option"
          aria-selected={i === highlight}
          data-index={i}
          // mousedown sem foco: a caixa de texto não perde o cursor no clique
          onMouseDown={(e) => e.preventDefault()}
          onMouseEnter={() => onHighlight(i)}
          onClick={() => onChoose(reply)}
          className={cn(
            'cursor-pointer px-3 py-1.5',
            i === highlight ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/60'
          )}
        >
          <div className="flex min-w-0 items-center gap-2">
            <code className="shrink-0 rounded-sm bg-muted px-1 font-mono text-xs text-foreground">
              /{reply.shortcut}
            </code>
            <span className="min-w-0 truncate text-sm font-medium">{reply.title}</span>
            {reply.desk ? (
              <span className="ml-auto shrink-0 text-[11px] text-muted-foreground">
                {reply.desk.name}
              </span>
            ) : null}
          </div>
          <p className="mt-0.5 line-clamp-2 text-xs whitespace-pre-line text-muted-foreground">
            {fillQuickReply(reply.body, variables).text}
          </p>
        </li>
      ))}
    </ul>
  );
}
